/**
 * AdminAcquisitionTab - Channel-level acquisition quality table.
 * Sortable by any column. Click a channel to drill down into campaigns/creatives.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { safeLocaleString } from '@/lib/utils';
import {
  getDashboardChannels,
  getDashboardCampaigns,
  getDashboardUtmBreakdown,
  type ChannelRow,
  type CampaignRow,
  type UtmBreakdownRow,
} from '@/services/adminDashboardService';
import { ArrowUpDown, ArrowLeft } from 'lucide-react';

const PERIODS = [
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

type ChannelSortKey = 'channel' | 'accounts' | 'carousels' | 'checkouts' | 'payments' | 'revenue' | 'convRate' | 'activationRate';
type CampaignSortKey = 'utm_campaign' | 'utm_content' | 'accounts' | 'carousels' | 'checkouts' | 'payments' | 'revenue' | 'convRate';

const AdminAcquisitionTab: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<ChannelSortKey>('accounts');
  const [sortAsc, setSortAsc] = useState(false);

  // Drill-down state
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campSortKey, setCampSortKey] = useState<CampaignSortKey>('accounts');
  const [campSortAsc, setCampSortAsc] = useState(false);

  // UTM breakdown
  const [utmRows, setUtmRows] = useState<UtmBreakdownRow[]>([]);
  const [showUtm, setShowUtm] = useState(false);
  const [utmFilter, setUtmFilter] = useState('');

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDashboardChannels(period);
      setChannels(data.channels);
    } catch (err) {
      console.error('Failed to fetch channel data', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchChannels();
    setSelectedChannel(null);
    setShowUtm(false);
  }, [fetchChannels]);

  const fetchUtmBreakdown = useCallback(async () => {
    try {
      const res = await getDashboardUtmBreakdown(period);
      setUtmRows(res.data || []);
    } catch {
      setUtmRows([]);
    }
  }, [period]);

  const handleToggleUtm = () => {
    if (!showUtm && utmRows.length === 0) fetchUtmBreakdown();
    setShowUtm(!showUtm);
  };

  const fetchCampaigns = useCallback(async (channel: string) => {
    setCampaignLoading(true);
    try {
      const data = await getDashboardCampaigns(period, channel);
      setCampaigns(data.data);
    } catch (err) {
      console.error('Failed to fetch campaign data', err);
    } finally {
      setCampaignLoading(false);
    }
  }, [period]);

  const handleChannelClick = (channel: string) => {
    setSelectedChannel(channel);
    setCampSortKey('accounts');
    setCampSortAsc(false);
    fetchCampaigns(channel);
  };

  const handleBack = () => {
    setSelectedChannel(null);
    setCampaigns([]);
  };

  const handleSort = (key: ChannelSortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const handleCampSort = (key: CampaignSortKey) => {
    if (campSortKey === key) setCampSortAsc(!campSortAsc);
    else { setCampSortKey(key); setCampSortAsc(false); }
  };

  const getChannelVal = (ch: ChannelRow, key: ChannelSortKey): number | string => {
    switch (key) {
      case 'convRate': return ch.accounts > 0 ? ch.payments / ch.accounts : 0;
      case 'activationRate': return ch.accounts > 0 ? ch.carousels / ch.accounts : 0;
      case 'channel': return ch.channel;
      default: return ch[key];
    }
  };

  const getCampVal = (c: CampaignRow, key: CampaignSortKey): number | string => {
    switch (key) {
      case 'convRate': return c.accounts > 0 ? c.payments / c.accounts : 0;
      case 'utm_campaign': return c.utm_campaign || '(vazio)';
      case 'utm_content': return c.utm_content || '(vazio)';
      default: return c[key];
    }
  };

  const sortedChannels = [...channels].sort((a, b) => {
    const va = getChannelVal(a, sortKey);
    const vb = getChannelVal(b, sortKey);
    if (typeof va === 'string' && typeof vb === 'string') {
      return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    return sortAsc ? (va as number) - (vb as number) : (vb as number) - (va as number);
  });

  const sortedCampaigns = [...campaigns].sort((a, b) => {
    const va = getCampVal(a, campSortKey);
    const vb = getCampVal(b, campSortKey);
    if (typeof va === 'string' && typeof vb === 'string') {
      return campSortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    return campSortAsc ? (va as number) - (vb as number) : (vb as number) - (va as number);
  });

  if (loading) {
    return <div className="text-muted-foreground py-8 text-center">Carregando dados...</div>;
  }

  const SortHeader = ({ label, sortField, onClick, activeKey, asc }: {
    label: string;
    sortField: string;
    onClick: (key: any) => void;
    activeKey: string;
    asc: boolean;
  }) => (
    <th
      className="text-right px-4 py-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
      onClick={() => onClick(sortField)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${activeKey === sortField ? 'text-foreground' : ''}`} />
      </span>
    </th>
  );

  // ================================================================
  // CAMPAIGN DRILL-DOWN VIEW
  // ================================================================
  if (selectedChannel) {
    return (
      <div className="space-y-6">
        {/* Period + back button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
          <div className="flex gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => { setPeriod(p.value); fetchCampaigns(selectedChannel); }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <h3 className="text-sm font-semibold text-foreground">
          Campanhas de: <span className="text-primary">{selectedChannel}</span>
        </h3>

        {campaignLoading ? (
          <div className="text-muted-foreground py-8 text-center">Carregando campanhas...</div>
        ) : campaigns.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            Nenhuma campanha encontrada para este canal neste período.
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <SortHeader label="Campanha" sortField="utm_campaign" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Criativo" sortField="utm_content" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Contas" sortField="accounts" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Carrosséis" sortField="carousels" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Checkouts" sortField="checkouts" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Pagamentos" sortField="payments" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Conversão" sortField="convRate" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                    <SortHeader label="Receita" sortField="revenue" onClick={handleCampSort} activeKey={campSortKey} asc={campSortAsc} />
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.map((c, i) => {
                    const convRate = c.accounts > 0 ? ((c.payments / c.accounts) * 100).toFixed(1) : '0';
                    return (
                      <tr key={`${c.utm_campaign}-${c.utm_content}-${i}`} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3 font-medium text-foreground text-left">{c.utm_campaign || '(vazio)'}</td>
                        <td className="px-4 py-3 text-muted-foreground text-left">{c.utm_content || '(vazio)'}</td>
                        <td className="text-right px-4 py-3 text-foreground">{c.accounts}</td>
                        <td className="text-right px-4 py-3 text-foreground">{c.carousels}</td>
                        <td className="text-right px-4 py-3 text-foreground">{c.checkouts}</td>
                        <td className="text-right px-4 py-3 text-foreground">{c.payments}</td>
                        <td className="text-right px-4 py-3 font-medium text-foreground">{convRate}%</td>
                        <td className="text-right px-4 py-3 text-foreground">
                          R$ {safeLocaleString(Number(c.revenue))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ================================================================
  // CHANNEL-LEVEL VIEW (default)
  // ================================================================
  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              period === p.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {channels.length === 0 ? (
        <div className="text-muted-foreground py-8 text-center">
          Nenhum dado de aquisição neste período.
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">Clique em um canal para ver campanhas e criativos.</p>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th
                      className="text-left px-4 py-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('channel')}
                    >
                      Canal
                    </th>
                    <SortHeader label="Contas" sortField="accounts" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                    <SortHeader label="Carrosséis" sortField="carousels" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                    <SortHeader label="Ativação" sortField="activationRate" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                    <SortHeader label="Checkouts" sortField="checkouts" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                    <SortHeader label="Pagamentos" sortField="payments" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                    <SortHeader label="Conversão" sortField="convRate" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                    <SortHeader label="Receita" sortField="revenue" onClick={handleSort} activeKey={sortKey} asc={sortAsc} />
                  </tr>
                </thead>
                <tbody>
                  {sortedChannels.map((ch) => {
                    const activationRate = ch.accounts > 0 ? ((ch.carousels / ch.accounts) * 100).toFixed(1) : '0';
                    const convRate = ch.accounts > 0 ? ((ch.payments / ch.accounts) * 100).toFixed(1) : '0';

                    return (
                      <tr
                        key={ch.channel}
                        className="border-t border-border hover:bg-muted/30 cursor-pointer"
                        onClick={() => handleChannelClick(ch.channel)}
                      >
                        <td className="px-4 py-3 font-medium text-primary hover:underline">{ch.channel}</td>
                        <td className="text-right px-4 py-3 text-foreground">{ch.accounts}</td>
                        <td className="text-right px-4 py-3 text-foreground">{ch.carousels}</td>
                        <td className="text-right px-4 py-3 text-foreground">{activationRate}%</td>
                        <td className="text-right px-4 py-3 text-foreground">{ch.checkouts}</td>
                        <td className="text-right px-4 py-3 text-foreground">{ch.payments}</td>
                        <td className="text-right px-4 py-3 font-medium text-foreground">{convRate}%</td>
                        <td className="text-right px-4 py-3 text-foreground">
                          R$ {safeLocaleString(ch.revenue)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* UTM Breakdown toggle */}
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          onClick={handleToggleUtm}
          className="w-full flex items-center justify-between p-4 text-sm font-semibold text-foreground hover:bg-muted/30 transition-colors"
        >
          <span>UTMs Recebidas (dados brutos)</span>
          <span className="text-xs text-muted-foreground">
            {showUtm ? 'Ocultar' : 'Expandir'}
          </span>
        </button>

        {showUtm && (() => {
          const q = utmFilter.toLowerCase();
          const filtered = q
            ? utmRows.filter((r) =>
                r.utm_source.toLowerCase().includes(q) ||
                r.utm_medium.toLowerCase().includes(q) ||
                r.utm_campaign.toLowerCase().includes(q) ||
                r.utm_content.toLowerCase().includes(q) ||
                r.derived_channel.toLowerCase().includes(q)
              )
            : utmRows;

          return (
            <div className="border-t border-border">
              {/* Filter input */}
              <div className="p-3 border-b border-border">
                <input
                  type="text"
                  placeholder="Filtrar por source, medium, campaign, content ou canal..."
                  value={utmFilter}
                  onChange={(e) => setUtmFilter(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground"
                />
              </div>

              {filtered.length === 0 ? (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  {utmRows.length === 0 ? 'Nenhum dado de UTM neste período.' : 'Nenhum resultado para este filtro.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">utm_source</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">utm_medium</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">utm_campaign</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">utm_content</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Canal derivado</th>
                        <th className="text-right px-4 py-2 font-medium text-muted-foreground">Contas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, i) => (
                        <tr key={i} className="border-t border-border hover:bg-muted/30">
                          <td className="px-4 py-2 font-mono text-xs text-foreground">{row.utm_source}</td>
                          <td className="px-4 py-2 font-mono text-xs text-foreground">{row.utm_medium}</td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{row.utm_campaign}</td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{row.utm_content}</td>
                          <td className="px-4 py-2 text-sm text-primary font-medium">{row.derived_channel}</td>
                          <td className="text-right px-4 py-2 text-foreground">{row.accounts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-3 bg-muted/30 text-xs text-muted-foreground">
                <span>
                  Mostrando {filtered.length} de {utmRows.length} combinações de UTM.
                  O "Canal derivado" é o mapeamento automático — valores como "bing/cpc" indicam UTMs sem regra específica.
                </span>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default AdminAcquisitionTab;
